/**
 * Color utilities for diagram shapes.
 */

/**
 * Parse a hex color string into RGB components.
 * Supports #RGB and #RRGGBB formats.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const cleaned = hex.replace("#", "");
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

/**
 * Calculate relative luminance of a color per WCAG 2.0.
 * Returns 0 (black) to 1 (white).
 */
function relativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Given a background color (hex string), return the best text color
 * for readability: "#000000" for light backgrounds, "#ffffff" for dark.
 *
 * If the color is empty/invalid, returns undefined (use theme default).
 */
export function getContrastTextColor(bgColor: string | undefined): string | undefined {
  if (!bgColor) return undefined;
  const rgb = hexToRgb(bgColor);
  if (!rgb) return undefined;
  const lum = relativeLuminance(rgb.r, rgb.g, rgb.b);
  // Threshold 0.179 is the standard WCAG midpoint for contrast
  return lum > 0.179 ? "#000000" : "#ffffff";
}
